import { Filter, QueryOptions } from "odata-query";
import { invert, mapValues } from "remeda";

export type { QueryOptions } from "odata-query";

export interface ServiceConfig {
  host: string;
  /** credentials is optional since some requests can be made without them */
  credentials?: Credentials;
  /** requestInterceptor allows you to modify the request before it is sent */
  requestInterceptor?: (request: Request) => Request;
  /** responseInterceptor allows you to modify the response before it is returned */
  responseInterceptor?: (request: Request, response: Response) => Response;
}

export interface Credentials {
  account?: string;
  token: string;
}

export type RequestOptions = Omit<RequestInit, "method" | "body">;

export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "APIError";
  }
}

export interface StatusResponse {
  status: true;
}

export interface CreateThreatProps {
  name: string;
  published: string;
  id?: string;
  source_id?: string;
  source?: string;
  tests?: string;
  ai_generated?: boolean;
}

export interface UpdateThreatProps {
  threat_id: string;
  name?: string;
  source_id?: string;
  source?: string;
  published?: string;
  tests?: string;
  ai_generated?: boolean;
}

export interface Threat {
  author: string | null;
  account_id: string;
  id: string;
  source_id: string | null;
  name: string;
  source: string | null;
  published: string | null;
  tests: string[];
  techniques: string[];
  tombstoned: string | null;
  created: string;
}

export interface CloneTestProps {
  source_test_id: string;
}

export interface CreateTestProps {
  ai_generated?: boolean;
  intel_context?: string;
  name: string;
  technique?: string;
  testId?: string;
  unit: string;
}

export interface UpdateTestProps {
  expected?: PartnerExpected;
  intel_context?: string;
  name?: string;
  technique?: string;
  testId: string;
  unit?: string;
}

export interface PartnerExpected {
  crowdstrike: EDRResponse;
}

export interface Test {
  author: string | null;
  account_id: string;
  created: string;
  expected: PartnerExpected;
  id: string;
  intel_context: string | null;
  name: string;
  supported_platforms: NonArchPlatform[];
  technique: string | null;
  tombstoned: string | null;
  unit: string;
}

export type AttachedTest = Test & {
  attachments: string[];
};

export type ListTestResponse = Test & {
  detection_platforms: SigmaNonArchPlatform[];
};

export interface UploadProps {
  testId: string;
  filename: string;
  data: BodyInit;
  ai_generated?: boolean;
}

export interface UploadedAttachment {
  id: string;
  filename: string;
}

export interface Technique {
  category: string | null;
  id: string;
  name: string;
  description: string;
  expected: PartnerExpected;
  relevant_categories: ControlCategory[];
}

export interface Terms {
  threat_intel?: Record<string, string>;
}

export interface User {
  handle: string;
  permission: Permission;
  name: string;
  oidc: OIDC;
  subscriptions: string[];
  terms: Terms;
  last_seen: string | null;
}

export interface TokenUser {
  handle: string;
  oidc: OIDC;
  name: string;
  permission: typeof Permissions.SERVICE;
  last_seen: string | null;
}

export interface CreateServiceUserResponse {
  handle: string;
  name: string;
  token: string;
}

export interface Control {
  api: string;
  created: string;
  id: ControlCode;
  instance_id: string;
  name: string | null;
  username: string;
}

export interface ValidControl extends Control {
  id: ValidControlCode;
}

export type Feature = keyof Account["features"];

export interface Account {
  account_id: string;
  whoami: {
    handle: string;
    oidc: OIDC;
  };
  users: User[];
  token_users: TokenUser[];
  queue: Queue[];
  controls: Control[];
  mode: Mode;
  slug: string;
  company: string;
  probe_sleep: string;
  oidc?: OIDCSettings;
  features: {
    detect: boolean;
    detections: boolean;
    observed_detected: boolean;
    paying: boolean;
    policy_evaluator: boolean;
    threat_intel: boolean;
    trial: boolean;
  };
}

export interface AccountItem {
  account_id: string;
  company: string;
  slug: string;
}

export interface OIDCSettings {
  client_id: string;
  created: string;
  domain: string;
  issuer: OIDCIssuers;
  oidc_url: string;
}

export type ThreatIntel =
  | ThreatIntelComplete
  | ThreatIntelFailed
  | ThreatIntelRunning;

export type ThreatIntelRunning = ThreatIntelParsing | ThreatIntelGenerating;

export interface ThreatIntelParsing {
  status: "RUNNING";
  step: "PARSE";
}

export interface ThreatIntelGenerating {
  status: "RUNNING";
  step: "GENERATE";
  num_tasks: number;
  completed_tasks: number;
}

export interface ThreatIntelFailed {
  status: "FAILED";
  reason: "FAILED" | "TIMED_OUT" | "ABORTED";
}
export interface ThreatIntelComplete {
  status: "COMPLETE";
  output: ThreatIntelOutput[];
  title: string | null;
}

export interface ThreatIntelOutput {
  status: "SUCCEEDED" | "FAILED" | "SKIPPED";
  name: string;
  technique: string;
  ai_generated: AiGeneratedOutput | null;
  existing_test: ExistingTestOutput | null;
  excluded: ExcludedOutput | null;
}

export interface ExcludedOutput {
  classification: "EXCLUDED" | "TOO MANY TECHNIQUES";
}

export interface AiGeneratedOutput {
  go_code: string;
  readme: string;
  sigma_rules: string[];
  intel_context: string;
  threat_hunt_queries: ThreatHuntQueries[];
}

export interface ExistingTestOutput {
  go_code: string;
  readme: string | null;
  sigma_rules: string[];
  test_id: string;
  test_name: string;
  threat_hunt_queries: ThreatHuntQueries[];
}

export interface ThreatHuntQueries {
  name: string;
  query: string;
  control: ControlCode;
}

export interface VerifiedUser {
  account_id: string;
  token: string;
  handle: string;
  permission: Permission;
  name: string;
}

export interface CreateAccountParams {
  email: string;
  name?: string;
  company?: string;
  slug?: string;
  additionalFields?: Record<string, string>;
}

export interface CreateAccountUserResponse {
  account_id: string;
  temp_password: string;
  user_id: string;
}

export interface InviteUserParams {
  permission: PermissionName;
  email: string;
  name?: string;
  oidc: OIDC;
}

export interface UpdateCurrentUserParams {
  name?: string;
}

export interface UpdateUserParams {
  handle: string;
  permission?: PermissionName;
  oidc: OIDC;
}

export interface InviteUserResponse {
  handle: string;
  oidc: OIDC;
  permission: Permission;
}

export interface Queue {
  expires: string;
  run_code: RunCode;
  started: string;
  tag: string | null;
  test: string | null;
  threat: string | null;
}

/**
 * Get the name of an enum value
 * @example,
 * getEnumName(RunCodes, RunCodes.DAILY) // => "DAILY"
 */
export function getEnumName<T extends Record<string, unknown>>(
  enumType: T,
  value: T[keyof T],
): keyof T {
  const key = Object.keys(enumType).find((k) => enumType[k] === value);
  if (key === undefined) {
    throw new Error(`Unknown enum value ${value}`);
  }
  return key;
}

export const ValidRunCodes = {
  DAILY: 1,
  WEEKLY: 2,
  MONTHLY: 3,
  SMART: 4,
  DEBUG: 5,
  RUN_ONCE: 6,
  MONDAY: 10,
  TUESDAY: 11,
  WEDNESDAY: 12,
  THURSDAY: 13,
  FRIDAY: 14,
  SATURDAY: 15,
  SUNDAY: 16,
  MONTH_1: 20,
} as const;

export type ValidRunCode = (typeof ValidRunCodes)[keyof typeof ValidRunCodes];

export const RunCodes = {
  INVALID: -1,
  ...ValidRunCodes,
} as const;

export type RunCodeName = keyof typeof RunCodes;
export type RunCode = (typeof RunCodes)[keyof typeof RunCodes];

export const Permissions = {
  INVALID: -1,
  ADMIN: 0,
  EXECUTIVE: 1,
  BUILD: 2,
  SERVICE: 3,
  SUPPORT: 5,
} as const;

export type PermissionName = keyof typeof Permissions;
export type Permission = (typeof Permissions)[keyof typeof Permissions];

export const Modes = {
  MANUAL: 0,
  FROZEN: 1,
  AUTOPILOT: 2,
} as const;

export type ModeName = keyof typeof Modes;
export type Mode = (typeof Modes)[keyof typeof Modes];

export interface EnabledTest {
  id: string;
}

export interface Probe {
  endpoint_id: string;
  host: string;
  serial_num: string;
  edr_id: string | null;
  control: ControlCode;
  tags: string[];
  last_seen: string | null;
  created: string;
  dos: Platform | null;
  os?: string | null;
  policy?: string | null;
  policy_name?: string | null;
}

export const ExitCodes = {
  MISSING: -1,
  UNKNOWN_ERROR: 1,
  MALFORMED_TEST: 2,
  UNREPORTED: 3,
  PROCESS_BLOCKED: 9,
  PROCESS_BLOCKED_GRACEFULLY: 15,
  PROTECTED: 100,
  UNPROTECTED: 101,
  TIMED_OUT: 102,
  FAILED_CLEANUP: 103,
  TEST_NOT_RELEVANT: 104,
  DYNAMIC_QUARANTINE: 105,
  BLOCKED_AT_PERIMETER: 106,
  EXPLOIT_PREVENTED: 107,
  ENDPOINT_NOT_RELEVANT: 108,
  INSUFFICIENT_PRIVILEGES: 109,
  INCORRECTLY_BLOCKED: 110,
  EXECUTION_PREVENTED: 126,
  STATIC_QUARANTINE: 127,
  BLOCKED: 137,
  UNEXPECTED_ERROR: 256,
} as const;

export type ExitCodeName = keyof typeof ExitCodes;
export type ExitCode = (typeof ExitCodes)[ExitCodeName];
export const ExitCodeNames = Object.keys(ExitCodes) as ExitCodeName[];
export const ExitCodeGroup = {
  NONE: [ExitCodes.MISSING],
  PROTECTED: [
    ExitCodes.BLOCKED,
    ExitCodes.BLOCKED_AT_PERIMETER,
    ExitCodes.DYNAMIC_QUARANTINE,
    ExitCodes.EXPLOIT_PREVENTED,
    ExitCodes.EXECUTION_PREVENTED,
    ExitCodes.PROCESS_BLOCKED,
    ExitCodes.PROCESS_BLOCKED_GRACEFULLY,
    ExitCodes.PROTECTED,
    ExitCodes.STATIC_QUARANTINE,
  ],
  UNPROTECTED: [ExitCodes.UNPROTECTED],
  ERROR: [
    ExitCodes.FAILED_CLEANUP,
    ExitCodes.INCORRECTLY_BLOCKED,
    ExitCodes.MALFORMED_TEST,
    ExitCodes.TIMED_OUT,
    ExitCodes.UNEXPECTED_ERROR,
    ExitCodes.UNKNOWN_ERROR,
    ExitCodes.UNREPORTED,
  ],
  NOT_RELEVANT: [
    ExitCodes.ENDPOINT_NOT_RELEVANT,
    ExitCodes.INSUFFICIENT_PRIVILEGES,
    ExitCodes.TEST_NOT_RELEVANT,
  ],
} as const;

export const ActionCodes = {
  NONE: 0,
  IGNORE: 1,
  IMPORTANT: 2,
} as const;

export type ActionCodeName = keyof typeof ActionCodes;
export type ActionCode = (typeof ActionCodes)[ActionCodeName];

export const ValidControlCategories = {
  CLOUD: 1,
  EMAIL: 2,
  IDENTITY: 3,
  NETWORK: 4,
  XDR: 5,
  ASSET_MANAGER: 6,
  DISCOVERED_DEVICES: 7,
  VULN_MANAGER: 8,
  SIEM: 9,
  PRIVATE_REPO: 10,
  HOST_FIREWALL: 11,
} as const;

export const ControlCategories = {
  INVALID: -1,
  ...ValidControlCategories,
} as const;

export type ValidControlCategory =
  (typeof ValidControlCategories)[keyof typeof ValidControlCategories];
export type ControlCategoryName = keyof typeof ControlCategories;
export type ControlCategory = (typeof ControlCategories)[ControlCategoryName];

export const ValidSCMCategories = {
  ENDPOINT: 1,
  INBOX: 2,
  USER: 3,
} as const;

export const SCMCategories = {
  INVALID: -1,
  NONE: 0,
  ...ValidSCMCategories,
} as const;

export type ValidSCMCategory =
  (typeof ValidSCMCategories)[keyof typeof ValidSCMCategories];
export type SCMCategory = (typeof SCMCategories)[keyof typeof SCMCategories];

export const ValidControlCodes = {
  CROWDSTRIKE: 1,
  DEFENDER: 2,
  SPLUNK: 3,
  SENTINELONE: 4,
  VECTR: 5,
  S3: 6,
  INTUNE: 7,
  SERVICENOW: 8,
  OKTA: 9,
  M365: 10,
  ENTRA: 11,
  JAMF: 12,
  CROWDSTRIKE_IDENTITY: 13,
  GMAIL: 14,
  GOOGLE_IDENTITY: 15,
  DEFENDER_DISCOVERY: 16,
  TENABLE: 17,
  EC2: 18,
  AWS_SSM: 19,
  AZURE_VM: 20,
  GITHUB: 21,
  TENABLE_DISCOVERY: 22,
  QUALYS: 23,
  QUALYS_DISCOVERY: 24,
  RAPID7: 25,
  RAPID7_DISCOVERY: 26,
  INTUNE_HOST_FIREWALL: 27,
} as const;

export type ValidControlCode =
  (typeof ValidControlCodes)[keyof typeof ValidControlCodes];

export const ControlCodes = {
  INVALID: -1,
  NONE: 0,
  ...ValidControlCodes,
} as const;

export type ControlCodeName = keyof typeof ControlCodes;
export type ControlCode = (typeof ControlCodes)[ControlCodeName];

export const ValidPartnerEvents = {
  REDUCED_FUNCTIONALITY_MODE: 1,
  NO_EDR: 2,
  MISSING_EDR_POLICY: 3,
  MISSING_AV_POLICY: 4,
  MISSING_MFA: 5,
  NO_ASSET_MANAGER: 6,
  MISCONFIGURED_POLICY_SETTING: 7,
  MISSING_SCAN: 8,
  OUT_OF_DATE_SCAN: 9,
  NO_VULN_MANAGER: 10,
  USER_MISSING_ASSET_MANAGER: 11,
  USER_MISSING_EDR: 12,
  USER_MISSING_VULN_MANAGER: 13,
  NO_SERVER_MANAGER: 14,
  NO_HOST_FIREWALL: 15,
  MISSING_HOST_FIREWALL_POLICY: 16,
  USER_MISSING_HOST_FIREWALL: 17,
} as const;

export type ValidPartnerEventCode =
  (typeof ValidPartnerEvents)[keyof typeof ValidPartnerEvents];

export const PartnerEvents = {
  INVALID: -1,
  ...ValidPartnerEvents,
} as const;

export type PartnerEventCodeName = keyof typeof PartnerEvents;
export type PartnerEventCode = (typeof PartnerEvents)[PartnerEventCodeName];

export const BehavesAsControlCodeMappings: Partial<
  Record<ValidControlCode, ValidControlCode>
> = {
  [ControlCodes.DEFENDER_DISCOVERY]: ControlCodes.DEFENDER,
  [ControlCodes.TENABLE_DISCOVERY]: ControlCodes.TENABLE,
  [ControlCodes.QUALYS_DISCOVERY]: ControlCodes.QUALYS,
  [ControlCodes.RAPID7_DISCOVERY]: ControlCodes.RAPID7,
  [ControlCodes.INTUNE_HOST_FIREWALL]: ControlCodes.INTUNE,
};

export const InheritBehaviorFromCodeMappings = mapValues(
  invert(BehavesAsControlCodeMappings),
  Number,
) as typeof BehavesAsControlCodeMappings;

export const EDRResponses = {
  INVALID: -1,
  OBSERVE: 1,
  DETECT: 2,
  PREVENT: 3,
} as const;

export type EDRResponseName = keyof typeof EDRResponses;
export type EDRResponse = (typeof EDRResponses)[EDRResponseName];

export const BackgroundJobType = {
  INVALID: -1,
  UPDATE_SCM: 1,
  DEPLOY_PROBE: 2,
  OBSERVED_DETECTED: 3,
  PRELUDE_ENDPOINT_SYNC: 4,
  EXPORT_SCM: 5,
  PARTNER_GROUPS: 6,
} as const;

export type BackgroundJobTypeName = keyof typeof BackgroundJobType;
export type BackgroundJobType =
  (typeof BackgroundJobType)[BackgroundJobTypeName];

export type Architecture = "arm64" | "x86_64";
export type NonArchPlatform = "darwin" | "linux" | "windows";
export type SigmaNonArchPlatform = "macos" | "linux" | "windows";

export const Platforms = [
  "linux-arm64",
  "linux-x86_64",
  "windows-arm64",
  "windows-x86_64",
  "darwin-arm64",
  "darwin-x86_64",
  "unknown",
] as const;

export type Platform = (typeof Platforms)[number];

export interface ActivityQuery {
  start: string;
  finish: string;
  tests?: string;
  threats?: string;
  endpoints?: string;
  dos?: Platform;
  control?: string;
  impersonate?: string;
  os?: string;
  policy?: string;
  statuses?: string;
}

export type ActivityQueryKey = keyof ActivityQuery;

export interface Activity {
  control: ControlCode;
  date: string;
  detected: string | null;
  dos: Platform;
  endpoint_id: string;
  id: string;
  observed: string | null;
  os: string | null;
  policy: string | null;
  status: ExitCode;
  technique: string | null;
  test: string;
  threat: string | null;
}

export interface PersonalActivity {
  detected: number;
  observed: number;
  partner_total: number;
  prelude_total: number;
  prevented: number;
}

export interface TestsActivity {
  expected: PartnerExpected;
  id: string;
  personal: PersonalActivity;
  social: number | null;
  /** @deprecated */
  protected: number | null;
}

export interface TechniquesActivity {
  expected: PartnerExpected;
  id: string;
  name: string | null;
  personal: PersonalActivity;
  social: number | null;
  /** @deprecated */
  protected: number | null;
}

export interface ThreatsActivity {
  endpoints: number;
  id: string;
  prelude_total: number;
  prevented: number;
  social: number | null;
  /** @deprecated */
  protected: number | null;
}

export type EndpointActivity = {
  id: string;
  prelude_total: number;
  prevented: number;
  /** @deprecated */
  protected: number;
};

export interface ProtectedActivity {
  date: string;
  protected: number;
}

export interface MetricsActivity {
  account_id: string;
  endpoints: number;
  tests: number;
  company: string;
  unique_tests: number;
}

export interface Finding {
  key: string;
  value: string | ControlCode | null;
  improvement: number;
}

export interface FindingsActivity {
  protected: number;
  dos: Platform;
  control: ControlCode;
  policy: string | null;
  os: string | null;
  finding?: Finding;
}

export interface RegisterEndpointParams {
  host: string;
  serial_num: string;
  tags?: string;
}

export interface UpdateEndpointParams {
  endpoint_id: string;
  tags?: string;
}

export interface UpdatedEndpoint {
  id: string;
}

export interface DownloadParams {
  name: string;
  dos: Platform;
}

export interface AttachPartnerParams {
  partnerCode: ControlCode;
  api?: string;
  user: string;
  secret?: string;
  instanceId?: string;
  name?: string;
}

export interface AttachedPartner {
  api: string;
  connected: boolean;
}

export interface RenamePartnerParams {
  partnerCode: ControlCode;
  instanceId: string;
  name: string;
}

export interface LegacyEndpointsParams {
  partner: ControlCodeName;
  platform: string;
  hostname?: string;
  offset?: number;
  count?: number;
}

export type ODataQuery<T> = Partial<
  Pick<QueryOptions<T>, "filter" | "top" | "skip" | "orderBy">
>;

export interface ODataRequestParams<T> {
  query?: ODataQuery<T>;
}

export type EndpointsParams = ODataRequestParams<PartnerEndpoints>;
export type UsersParams = ODataRequestParams<PartnerUsers>;
export type InboxesParams = ODataRequestParams<PartnerInboxes>;

export interface CreateObjectExceptionParams {
  category: ControlCategory;
  name: string;
  filter: Filter<PartnerEndpoints | PartnerUsers | PartnerInboxes>;
  expires: Date | undefined;
}

export interface UpdateObjectExceptionParams {
  id: string;
  name: string;
  filter: Filter<PartnerEndpoints | PartnerUsers | PartnerInboxes>;
  expires: Date | undefined;
}

export interface CreatePolicyExceptionParams {
  control: ControlCode;
  instanceId: string;
  policyId: string;
  settingNames: string[];
  expires?: Date;
}

export interface UpdatePolicyExceptionParams {
  control: ControlCode;
  instanceId: string;
  policyId: string;
  settingNames?: string[];
  expires?: Date;
}

export interface DeletePolicyExceptionParams {
  instanceId: string;
  policyId: string;
  control: ControlCode;
}

export interface DeployParams {
  partnerCode: ControlCode;
  hostIds: string[];
}

export interface DeployedEndpoints {
  id: ControlCode;
  host_ids: string[];
}

export type LegacyPartnerEndpoints = Record<
  string,
  {
    hostname: string;
    os: string;
    policy?: string | null;
    platform?: string | null;
    reduced_functionality_mode?: string | null;
    policy_name?: string | null;
  }
>;

export type PartnerControlFailure = {
  control: ValidControlCode | null;
  excepted: boolean;
  event: PartnerEventCode;
  instance_id: string | null;
  seconds_in_state: number;
  start_time: string;
};

export type PaginatedResponse<T> = {
  data: T[];
  total_count: number;
};

export type PartnerEndpointsControl = {
  category: ControlCategory;
  cloud_account_id: string | null;
  cloud_instance_id: string | null;
  cloud_region: string | null;
  control: ValidControlCode;
  device_ownership: string | null;
  device_type: string | null;
  has_agent?: boolean;
  hostname: string;
  instance_id: string;
  last_scan: string | null;
  last_seen: string | null;
  mac_addresses: string[] | null;
  missing_scan?: boolean;
  no_av_policy: boolean | null;
  no_edr_policy: boolean | null;
  operational_state: string | null;
  out_of_date_scan?: boolean;
  platform: string | null;
  policies?: string[];
  primary_user_email?: string;
  primary_user_id?: string;
  primary_user_name?: string;
  reduced_functionality_mode: boolean | null;
  serial_number: string | null;
  version: string | null;
};

export interface PartnerControlGroup {
  name: string;
  type: string;
  control: ValidControlCode;
  description: string | null;
  instance_id: string;
  membership_rule: string | null;
}

export type PartnerEndpoints = {
  controls: ValidControlCode[];
  device_types: string[] | null;
  endpoint_id: string;
  excepted: ExceptedCategory[];
  failures: PartnerControlFailure[] | null;
  groups: PartnerControlGroup[] | null;
  instance_ids: string[];
  instances: PartnerEndpointsControl[];
  last_seen: string | null;
  missing_asset_manager: boolean;
  missing_edr: boolean;
  missing_host_firewall: boolean;
  missing_server_manager: boolean;
  missing_vuln_manager: boolean;
  missing_vuln_scan: boolean;
  cloud_account_id: string | null;
  cloud_instance_id: string | null;
  cloud_region: string | null;
  hostname: string | null;
  mac_addresses: string[] | null;
  serial_number: string | null;
  platforms: string[] | null;
  primary_user_emails: string[] | null;
};

export type PartnerEndpointsResponse = PaginatedResponse<PartnerEndpoints>;

export type PartnerEndpointQueryKey =
  | keyof PartnerEndpoints
  | keyof PartnerEndpoints["instances"][number];

export type PartnerUsers = {
  controls: ValidControlCode[];
  devices: PartnerUsersDevice[] | null;
  email: string;
  excepted: ExceptedCategory[];
  failures: PartnerControlFailure[] | null;
  groups: PartnerControlGroup[] | null;
  instance_ids: string[];
  instances: {
    category: ControlCategory;
    control: ValidControlCode;
    instance_id: string;
    last_seen: string | null;
    missing_mfa: boolean;
    policies: string[];
    risk: string | null;
    risk_reason: string | null;
    status: string;
    user_type: string;
  }[];
  last_seen: string | null;
  missing_asset_manager: boolean;
  missing_edr: boolean;
  missing_vuln_manager: boolean;
  risks: string[] | null;
  secondary_emails: string[] | null;
  statuses: string[] | null;
  user_id: string;
  user_types: string[] | null;
};

export type PartnerUsersDevice = {
  hostname: string;
  serial_number: string;
  missing_asset_manager: boolean;
  missing_edr: boolean;
  missing_vuln_manager: boolean;
};

export type PartnerUsersResponse = PaginatedResponse<PartnerUsers>;

export type PartnerUserQueryKey =
  | keyof PartnerUsers
  | keyof PartnerUsers["instances"][number];

export type PartnerInboxes = {
  controls: ValidControlCode[];
  email: string;
  excepted: ExceptedCategory[];
  domain: string | null;
  failures: PartnerControlFailure[] | null;
  groups: PartnerControlGroup[] | null;
  inbox_types: string[] | null;
  instance_ids: string[];
  instances: {
    control: ValidControlCode;
    domain: string;
    inbox_type: string;
    instance_id: string;
    policies: string[];
    principal_name: string;
  }[];
  user_id: string;
  principal_names: string[] | null;
};

export type PartnerInboxesResponse = PaginatedResponse<PartnerInboxes>;

export type PartnerInboxQueryKey =
  | keyof PartnerInboxes
  | keyof PartnerInboxes["instances"][number];

export type ObjectException = {
  id: string;
  name: string | null;
  category: ControlCategory;
  filter: string;
  expires: string | null;
};

export type PolicyException = {
  author: string;
  control: ValidControlCode;
  category: ControlCategory;
  instance_id: string;
  policy_id: string;
  policy_name: string;
  policy_platform: NonArchPlatform;
  excepted_settings: ExceptedPolicySetting[];
  expires: string | null;
  created: string;
};

export type ExceptedPolicySetting = {
  setting_id: string;
  setting_name: string;
};

export interface AuditLog {
  event: string;
  account_id: string;
  user_id: string;
  values: AuditLogValues;
  status: string;
  timestamp: string;
}

export type AuditLogValues = Record<string, unknown>;

export interface BlockResponseRule {
  name: string;
  status: "CREATED" | "ALREADY_EXISTS" | "ERROR";
  created?: string;
  error?: string;
  ioa_id?: string;
  custom_detection_id?: string;
}

export interface BlockResponse {
  group_id?: string;
  platform: SigmaNonArchPlatform;
  rules: BlockResponseRule[];
}

export const OIDCOptions = ["google", "okta", "azure"] as const;
export type OIDCIssuers = (typeof OIDCOptions)[number];

export type OIDC = OIDCIssuers | string;

export interface AttachOIDC {
  issuer: OIDCIssuers;
  client_id: string;
  client_secret: string;
  oidc_url: string;
  email_attr: string;
}

export interface AttachedOIDC {
  issuer: OIDCIssuers;
  domain: string;
}

export interface ScheduleItem {
  id: string;
  type: "test" | "threat";
  runCode: RunCodeName;
  tags: string;
}

export interface UnscheduleItem {
  id: string;
  type: "test" | "threat";
  tags: string;
}

export interface DetectionRules {
  name: string;
  ruletype_id: string;
}

export interface CreateDetectionRule {
  testId: string;
  rule: string;
  detectionId?: string;
  ruleId?: string;
}

export interface Detection {
  account_id: string;
  created: string;
  id: string;
  name: string;
  rule_id: string;
  test: string;
  rule: Record<string, unknown>;
}

export interface DetectionReport {
  blocked: number;
  control: ControlCode;
  created: string;
  detected: number;
  detection_id: string;
  group_id: string;
  monitored: number;
  partner_detection_ids: string[];
  platform: SigmaNonArchPlatform;
  test_id: string;
}

export interface IoaStats {
  account_id: string;
  blocked: number;
  detected: number;
  detection_id: string;
  monitored: number;
}

export interface ObservedDetectedPerControl {
  account_id: string;
  control: ControlCode;
  detected: number;
  endpoint_ids: string[];
  observed: number;
  test_id: string;
}

export type ObservedDetected = Partial<
  Record<
    ControlCodeName & ("CROWDSTRIKE" | "DEFENDER"),
    ObservedDetectedPerControl[]
  >
>;

export interface GeneratedTechnique {
  parent_job_id: string | null;
  job_id: string;
}

export interface CompiledResponse {
  status: "COMPLETE" | "FAILED" | "RUNNING";
  results?: CompiledResponseResult[];
}

export interface CompiledResponseResult {
  platform: NonArchPlatform;
  architecture: Architecture;
  status: "SUCCEEDED" | "FAILED" | "SKIPPED";
  reason: string | null;
}

export interface ThreatHunt {
  control: ControlCode;
  created: string;
  id: string;
  name: string;
  query: string;
  test_id: string;
}

export interface ThreatHuntActivity {
  non_prelude_origin: number;
  prelude_origin: number;
  test_id: string;
}

export interface ThreatHuntResult {
  account_id: string;
  non_prelude_origin: number;
  prelude_origin: number;
  threat_hunt_id: string;
}

export interface PartnerAdvisoryParams {
  control: ControlCode;
  start?: string;
  offset?: number;
  limit?: number;
}

export interface PartnerAdvisory {
  created: string;
  description: string;
  id: string;
  name: string;
  tags: string[];
  slug: string;
}

export interface PartnerAdvisoryResponse {
  advisories: PartnerAdvisory[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface JobStatusPerJobType {
  control: ControlCode | null;
  end_time: string | null;
  error: string | null;
  id: string;
  instance_id: string | null;
  start_time: string;
  successful: boolean | null;
}

export type ListJobStatusResponse = Partial<
  Record<BackgroundJobTypeName, JobStatusPerJobType[]>
>;

export interface JobStatusResponse {
  control: ControlCode | null;
  end_time: string | null;
  error: string | null;
  id: string;
  instance_id: string | null;
  start_time: string;
  successful: boolean | null;
  job_type: BackgroundJobType;
  results?: { url: string };
}

export interface ControlMonitorSummaryResponse {
  endpoint_summary: ControlMonitorEndpointSummary;
  inbox_summary: ControlMonitorInboxSummary;
  user_summary: ControlMonitorUserSummary;
}

export interface ControlMonitorEndpointSummary {
  categories: ControlMonitorEndpointCategorySummary[];
  endpoint_count: number;
  missing_asset_manager_count: number;
  missing_edr_count: number;
  missing_host_firewall_count: number;
  missing_server_manager_count: number;
  missing_vuln_manager_count: number;
  missing_vuln_scan_count: number;
  excepted: {
    endpoint_count: number;
    missing_asset_manager_count: number;
    missing_edr_count: number;
    missing_host_firewall_count: number;
    missing_server_manager_count: number;
    missing_vuln_manager_count: number;
    missing_vuln_scan_count: number;
  };
}

export interface ControlMonitorEndpointCategorySummary {
  category: ValidControlCategory;
  instances: ControlMonitorEndpointControlSummary[];
  control_failure_count?: number;
  endpoint_count: number;
  missing_asset_manager_count?: number;
  missing_edr_count?: number;
  missing_host_firewall_count?: number;
  missing_server_manager_count?: number;
  missing_vuln_manager_count?: number;
  missing_vuln_scan_count?: number;
  excepted?: {
    endpoint_count: number;
    control_failure_count?: number;
    missing_asset_manager_count?: number;
    missing_edr_count?: number;
    missing_host_firewall_count?: number;
    missing_server_manager_count?: number;
    missing_vuln_manager_count?: number;
    missing_vuln_scan_count?: number;
  };
}

export interface ControlMonitorEndpointControlSummary {
  control: ValidControlCode;
  control_failure_count?: number;
  endpoint_count: number;
  instance_id: string;
  missing_agent_count?: number;
  missing_scan_count?: number;
  no_av_policy?: number;
  no_edr_policy?: number;
  no_host_firewall_policy?: number;
  out_of_date_scan_count?: number;
  policy_conflict_count?: number;
  reduced_functionality_mode?: number;
  setting_count?: number;
  setting_misconfiguration_count?: number;
  excepted: {
    control_failure_count?: number;
    endpoint_count: number;
    missing_agent_count?: number;
    missing_scan_count?: number;
    no_av_policy?: number;
    no_edr_policy?: number;
    no_host_firewall_policy?: number;
    out_of_date_scan_count?: number;
    reduced_functionality_mode?: number;
    setting_misconfiguration_count?: number;
  };
}

export interface TechniqueSummaryResponse {
  technique: string;
  instances: {
    control: ControlCode;
    instance_id: string;
    setting_count: number;
    setting_misconfiguration_count: number;
    excepted: {
      setting_count: number;
      setting_misconfiguration_count: number;
    };
  }[];
}

export interface ControlMonitorInboxSummary {
  categories: ControlMonitorInboxCategorySummary[];
  inbox_count: number;
  excepted: { inbox_count: number };
}

export interface ControlMonitorInboxCategorySummary {
  category: ValidControlCategory;
  instances: ControlMonitorInboxControlSummary[];
  inbox_count: number;
  excepted: { inbox_count: number };
}

export interface ControlMonitorInboxControlSummary {
  control: ValidControlCode;
  inbox_count: number;
  instance_id: string;
  setting_count: number;
  setting_misconfiguration_count: number;
  excepted: {
    inbox_count: number;
    setting_misconfiguration_count: number;
  };
}

export interface ControlMonitorUserSummary {
  categories: ControlMonitorUserCategorySummary[];
  user_count: number;
  control_failure_count: number;
  any_endpoint_failure_count: number;
  all_endpoint_failure_count: number;
  excepted: {
    user_count: number;
    control_failure_count: number;
    any_endpoint_failure_count: number;
    all_endpoint_failure_count: number;
  };
}

export interface ControlMonitorUserCategorySummary {
  category: ValidControlCategory;
  instances: ControlMonitorUserControlSummary[];
  control_failure_count: number;
  any_endpoint_failure_count: number;
  all_endpoint_failure_count: number;
  user_count: number;
  excepted: {
    control_failure_count: number;
    any_endpoint_failure_count: number;
    all_endpoint_failure_count: number;
    user_count: number;
  };
}

export interface ControlMonitorUserControlSummary {
  control: ValidControlCode;
  control_failure_count: number;
  instance_id: string;
  all_endpoint_failure_count: number;
  any_endpoint_failure_count: number;
  missing_asset_manager_count: number;
  missing_edr_count: number;
  missing_host_firewall_count: number;
  missing_mfa_count: number;
  missing_vuln_manager_count: number;
  user_count: number;
  setting_count: number;
  setting_misconfiguration_count: number;
  excepted: {
    control_failure_count: number;
    all_endpoint_failure_count: number;
    any_endpoint_failure_count: number;
    missing_asset_manager_count: number;
    missing_edr_count: number;
    missing_host_firewall_count: number;
    missing_mfa_count: number;
    missing_vuln_manager_count: number;
    user_count: number;
    setting_misconfiguration_count: number;
  };
}

export interface ControlMonitorEndpointPolicies {
  policies: ControlMonitorEndpointPolicy[];
}

export interface ControlMonitorUserPolicies {
  policies: ControlMonitorUserPolicy[];
}

export interface ControlMonitorInboxPolicies {
  total_inbox_count: number;
  policies: ControlMonitorInboxPolicy[];
}

export interface ControlMonitorPoliciesResponse {
  endpoint_evaluation?: ControlMonitorEndpointPolicies;
  inbox_evaluation?: ControlMonitorInboxPolicies;
  user_evaluation?: ControlMonitorUserPolicies;
}

export type ExceptedCategory = {
  category: ControlCategory;
  excepted: boolean;
};

export interface ControlMonitorEndpointPolicy {
  id: string;
  name: string;
  platform: NonArchPlatform;
  endpoint_count: number;
  conflict_count?: number;
  success_count: number;
  score: number;
  settings: ControlMonitorPolicySetting[];
}

export interface ControlMonitorUserPolicy {
  id: string;
  name: string;
  user_count: number;
  score: number;
  settings: ControlMonitorPolicySetting[];
}

export interface ControlMonitorInboxPolicy {
  id: string;
  name: string;
  inbox_count: number;
  score: number;
  settings: ControlMonitorPolicySetting[];
}

export interface ControlMonitorPolicySetting {
  id: string;
  name: string;
  excepted: boolean;
  critical: boolean;
  meets_recommendation: boolean;
  recommendation: string;
  techniques: string[];
  value: string;
}

export interface ControlMonitorSummaryParams {
  endpointsQuery?: ODataQuery<PartnerEndpoints>;
  usersQuery?: ODataQuery<PartnerUsers>;
  inboxesQuery?: ODataQuery<PartnerInboxes>;
  techniques?: string[];
}

export interface ControlMonitorPoliciesParams {
  query?: ODataQuery<any>;
  techniques?: string[];
}

export interface ExportResponse {
  job_id: string;
}

export const AlertTypes = {
  NEW_REDUCED_FUNCTIONALITY_MODE_ENDPOINTS: 1,
  NEW_NO_EDR_ENDPOINTS: 2,
  NEW_MISSING_EDR_POLICY_ENDPOINTS: 3,
  NEW_MISSING_AV_POLICY_ENDPOINTS: 4,
  NEW_MISSING_MFA_USERS: 5,
  NEW_NO_ASSET_MANAGER_ENDPOINTS: 6,
  NEW_POLICY_SETTING_FAILURE: 7,
  NEW_POLICY_SETTING_PASS: 8,
  NEW_MISSING_SCAN_ENDPOINTS: 9,
  NEW_NO_VULN_MANAGER_ENDPOINTS: 10,
  NEW_OUT_OF_DATE_SCAN_ENDPOINTS: 11,
  NEW_USER_MISSING_ASSET_MANAGER: 12,
  NEW_USER_MISSING_EDR: 13,
  NEW_USER_MISSING_VULN_MANAGER: 14,
  NEW_NO_SERVER_MANAGER_ENDPOINTS: 15,
  NEW_NO_HOST_FIREWALL_ENDPOINTS: 16,
  NEW_MISSING_HOST_FIREWALL_POLICY_ENDPOINTS: 17,
  NEW_USER_MISSING_HOST_FIREWALL: 18,
} as const;

export type AlertTypeName = keyof typeof AlertTypes;
export type AlertType = (typeof AlertTypes)[AlertTypeName];

export const PolicyTypes = {
  INVALID: 0,
  EDR: 1,
  AV: 2,
  IDENTITY_PASSWORD: 3,
  EMAIL_ANTIPHISH: 4,
  EMAIL_OUTBOUND: 5,
  EMAIL_CONTENT: 6,
  EMAIL_MALWARE: 7,
  EMAIL_ATTACHMENT: 8,
  EMAIL_LINKS: 9,
  EMAIL_DKIM: 10,
  DEVICE_COMPLIANCE: 11,
  IDENTITY_MFA: 12,
  HOST_FIREWALL: 13,
} as const;

export type PolicyTypeName = keyof typeof PolicyTypes;
export type PolicyType = (typeof PolicyTypes)[PolicyTypeName];

export type AlertAffectedEndpoint = {
  endpoint_id: string;
  control: ControlCode | null;
  end_time: string | null;
  hostname: string;
  serial_number: string | null;
  start_time: string;
};

export type AlertAffectedUser = {
  user_id: string;
  control: ControlCode | null;
  email: string;
  end_time: string | null;
  start_time: string;
};

export type AlertAffectedPolicy = {
  control: ControlCode;
  end_time: string | null;
  platform: string;
  policy_id: string;
  policy_name: string;
  recommendation: string;
  setting_name: string;
  start_time: string;
  value: string;
};

export type AlertDetails = {
  count: number;
};

export type AlertAffectedResource =
  | AlertAffectedEndpoint
  | AlertAffectedUser
  | AlertAffectedPolicy;

export type Alert = {
  id: string;
  read_at: string | null;
  created_at: string;
  alert_type: AlertType;
  details: AlertDetails;
};

export interface NotificationEmailConfig {
  emails: string[];
  subject: string;
  message: string;
}

export interface NotificationWebhookConfig {
  hook_urls: string[];
  message: string;
}

export interface Notification {
  author: string;
  created: string;
  email: NotificationEmailConfig | null;
  event: ValidPartnerEventCode;
  filter: string | null;
  id: string;
  last_message: string | null;
  run_code: ValidRunCode;
  scheduled_hour: number;
  slack: NotificationWebhookConfig | null;
  teams: NotificationWebhookConfig | null;
  control_category: ControlCategory;
  suppress_empty: boolean;
}

export interface SaveNotificationParams {
  email: NotificationEmailConfig | null;
  filter: Filter<PartnerEndpoints | PartnerUsers | PartnerInboxes> | null;
  id?: string;
  event: ValidPartnerEventCode;
  run_code: ValidRunCode;
  slack: NotificationWebhookConfig | null;
  teams: NotificationWebhookConfig | null;
  scheduled_hour: number;
  control_category: ControlCategory;
  suppress_empty: boolean;
}

export interface GeneratedSCMThreatIntel {
  title: string | null;
  techniques: string[] | null;
}

export interface CreateThreatParams {
  name: string;
  techniques: string;
  description?: string;
  id?: string;
  source?: string;
  source_id?: string;
  published?: string;
  generated?: boolean;
}

export interface UpdateThreatParams {
  name?: string;
  techniques?: string;
  description?: string;
  source?: string;
  source_id?: string;
  published?: string;
  generated?: boolean;
}

export interface SCMThreat {
  account_id: string;
  id: string;
  author: string;
  source: string;
  name: string;
  published: string;
  techniques: string[];
  description?: string;
  source_id?: string;
  generated?: boolean;
  created: string;
}

export interface View {
  author: string;
  category: ControlCategory;
  created: string;
  filter: string;
  id: string;
  name: string;
}

export interface CreateViewParams {
  category: ControlCategory;
  filter: string;
  name: string;
}

export interface UpdateViewParams {
  viewId: string;
  category?: ControlCategory;
  filter?: string;
  name?: string;
}

export interface ViewResponse {
  view_id: string;
}

export interface ForgotPasswordParams {
  handle: string;
  confirmation_code?: string;
  password?: string;
}

export interface ChangePasswordParams {
  new_password: string;
  current_password: string;
}

export interface PartnerGroup {
  id: string;
  description: string | null;
  display_name: string;
  members: GroupMember[] | null;
  membership_rule: string | null;
  type: string;
}

interface EndpointMember {
  hostname: string;
  platform: string;
  mac_addresses: string[] | null;
  serial_number: string | null;
  cloud_account_id: string | null;
  cloud_instance_id: string | null;
}

interface EmailMember {
  email: string;
}

type GroupMember = EmailMember | EndpointMember;

export type GroupParams = Omit<ODataRequestParams<Group>, "query"> & {
  query?: Omit<ODataQuery<Group>, "top" | "skip">;
};

export interface Group {
  control: ControlCode;
  description: string;
  group_id: string;
  name: string;
  instance_id: string;
  members: GroupMember[];
  membership_rule: string | null;
  type: string;
}

export interface UpdatePartnerGroupsParams {
  control: ControlCode;
  instanceId: string;
  groupIds: string[];
}
